import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const COLORS = {
  bg: '#F8FAFC',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  fuchsia: '#D946EF'
};

const steps = [
  {
    number: '1',
    title: 'Understand the problem',
    description: 'We embed with your team. Week 1.'
  },
  {
    number: '2',
    title: 'Build the system',
    description: 'Working software, not slides. Weeks 2-5.'
  },
  {
    number: '3',
    title: 'Ship and iterate',
    description: 'Week 6: You take the keys. Production-ready and fully owned by your team.'
  }
];

const headerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.18, delayChildren: 0.1 }
  }
};

const headerItemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  }
};

const stepVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.18,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const circleVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: (i) => ({
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.5,
      delay: i * 0.18 + 0.1,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const lineVariants = {
  hidden: { scaleY: 0 },
  visible: (i) => ({
    scaleY: 1,
    transition: {
      duration: 0.6,
      delay: i * 0.18 + 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const desktopLineVariants = {
  hidden: { scaleX: 0 },
  visible: (i) => ({
    scaleX: 1,
    transition: {
      duration: 0.8,
      delay: i * 0.18 + 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const HowWeBuild = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section
      id="how-we-build"
      ref={ref}
      style={{
        backgroundColor: COLORS.bg,
        color: COLORS.textPrimary,
        padding: 'var(--spacing-8xl) 0'
      }}
    >
      <div className="container">
        <motion.div
          variants={headerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          style={{ textAlign: 'center', marginBottom: 'var(--spacing-4xl)' }}
        >
          <motion.div variants={headerItemVariants}>
            <span
              style={{
                display: 'inline-block',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
                letterSpacing: 'var(--tracking-widest)',
                textTransform: 'uppercase',
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet}, ${COLORS.fuchsia})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 'var(--spacing-lg)'
              }}
            >
              Process
            </span>
          </motion.div>

          <motion.h2
            variants={headerItemVariants}
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              fontWeight: 700,
              color: COLORS.textPrimary,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              marginBottom: 'var(--spacing-lg)'
            }}
          >
            How We Build
          </motion.h2>

          <motion.p
            variants={headerItemVariants}
            style={{
              fontSize: 'var(--text-lg)',
              color: COLORS.textSecondary,
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            Three steps. No proposals. No discovery phases that never end.
          </motion.p>
        </motion.div>

        <div
          className="how-we-build-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            alignItems: 'flex-start',
            position: 'relative',
            gap: 'var(--spacing-2xl)'
          }}
        >
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              custom={index}
              variants={stepVariants}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              className="how-we-build-step"
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 'var(--spacing-md)'
              }}
            >
              <div
                className="how-we-build-visual"
                style={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                {index < steps.length - 1 && (
                  <motion.div
                    className="how-we-build-connector how-we-build-connector-desktop"
                    custom={index}
                    variants={desktopLineVariants}
                    initial="hidden"
                    animate={inView ? 'visible' : 'hidden'}
                    style={{
                      position: 'absolute',
                      top: '32px',
                      left: 'calc(50% + 40px)',
                      right: 'calc(-50% + 40px)',
                      height: '2px',
                      background: `linear-gradient(to right, ${COLORS.indigo}, ${COLORS.violet})`,
                      transformOrigin: 'left center',
                      zIndex: 0
                    }}
                  />
                )}

                <motion.div
                  custom={index}
                  variants={circleVariants}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.25 }}
                  className="how-we-build-circle"
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet}, ${COLORS.fuchsia})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    boxShadow: '0 10px 24px -8px rgba(99, 102, 241, 0.45)',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {step.number}
                </motion.div>

                {index < steps.length - 1 && (
                  <motion.div
                    className="how-we-build-connector how-we-build-connector-mobile"
                    custom={index}
                    variants={lineVariants}
                    initial="hidden"
                    animate={inView ? 'visible' : 'hidden'}
                    style={{
                      position: 'absolute',
                      top: '72px',
                      left: '50%',
                      width: '2px',
                      bottom: 'calc(-1 * var(--spacing-2xl))',
                      background: `linear-gradient(to bottom, ${COLORS.indigo}, ${COLORS.violet})`,
                      transformOrigin: 'top center',
                      zIndex: 0
                    }}
                  />
                )}
              </div>

              <div
                className="how-we-build-content"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)'
                }}
              >
                <h3
                  style={{
                    fontSize: 'var(--text-xl)',
                    fontWeight: 600,
                    color: COLORS.textPrimary,
                    letterSpacing: '-0.015em',
                    margin: 0
                  }}
                >
                  {step.title}
                </h3>

                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: COLORS.textSecondary,
                    lineHeight: 1.6,
                    margin: 0,
                    maxWidth: '260px'
                  }}
                >
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .how-we-build-grid {
            grid-template-columns: 1fr !important;
            gap: var(--spacing-2xl) !important;
          }
          .how-we-build-step {
            flex-direction: row !important;
            align-items: stretch !important;
            text-align: left !important;
            gap: var(--spacing-xl) !important;
          }
          .how-we-build-visual {
            width: 64px !important;
            flex-shrink: 0 !important;
            align-self: stretch !important;
            justify-content: flex-start !important;
          }
          .how-we-build-connector-desktop {
            display: none !important;
          }
          .how-we-build-connector-mobile {
            display: block !important;
          }
          .how-we-build-content {
            align-items: flex-start !important;
            padding-top: 8px !important;
            flex: 1 !important;
          }
          .how-we-build-content p {
            max-width: none !important;
          }
        }
        @media (min-width: 769px) {
          .how-we-build-connector-mobile {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default HowWeBuild;
